import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toCamelCase } from "../pendo/helpers/formatting";
import { isNonEmptyArray, isValidString } from "../pendo/helpers/validation";
import { useAccountData } from "./useAccountData";
/**
 * Helper to check if contract end date is valid (not the default "1900-01-01" date)
 */
const isValidContractEndDate = (dateString) => {
    return isValidString(dateString) && !dateString.startsWith("1900-01-01");
};
/**
 * Helper to safely parse a string value that might be JSON
 * Returns parsed value if valid JSON (object or array), otherwise returns original string
 */
const parseValueIfJson = (value) => {
    const trimmed = value.trim();
    if (!trimmed || (!trimmed.startsWith("[") && !trimmed.startsWith("{"))) {
        return value;
    }
    try {
        const parsed = JSON.parse(value);
        return typeof parsed === "object" && parsed !== null ? parsed : value;
    }
    catch (_a) {
        return value;
    }
};
/**
 * Helper to ensure product exists in map
 */
const ensureProduct = (map, productCode) => {
    if (!map[productCode]) {
        map[productCode] = {};
    }
};
/**
 * Transform raw account data into product-centric structure
 * This is a pure function that can be cached by React Query
 * Exported for use in App Router hooks
 */
export const transformAccountData = (rawAccountData) => {
    // Require at least account ID and name
    if (!rawAccountData ||
        !isValidString(rawAccountData.account_id) ||
        !isValidString(rawAccountData.name)) {
        return null;
    }
    // Get all unique product codes from licenses
    const productCodes = isNonEmptyArray(rawAccountData.licenses)
        ? [
            ...new Set(rawAccountData.licenses
                .map((license) => license.product_code)
                .filter(isValidString)),
        ]
        : [];
    // Extract industries as array of names
    const industries = isNonEmptyArray(rawAccountData.industries)
        ? rawAccountData.industries
            .map((industry) => industry.name)
            .filter(isValidString)
        : [];
    // Extract regions as array of names
    const regions = isNonEmptyArray(rawAccountData.regions)
        ? rawAccountData.regions.map((region) => region.name).filter(isValidString)
        : [];
    // Build products object with all data grouped by product code
    const productsMap = {};
    // Initialize products from licenses
    productCodes.forEach((productCode) => {
        productsMap[productCode] = {};
    });
    // 1. Extract availableRoles and licensedRegions from licenses
    if (isNonEmptyArray(rawAccountData.licenses)) {
        rawAccountData.licenses.forEach((license) => {
            const { product_code: productCode, roles, product } = license;
            if (!isValidString(productCode))
                return;
            // Extract roles
            if (isNonEmptyArray(roles)) {
                const availableRoles = roles
                    .map((role) => {
                    const { role: roleName, seats } = role;
                    if (!isValidString(roleName) || typeof seats !== "number") {
                        return null;
                    }
                    // Keep roles with seats > 0 OR seats === -1 (unlimited)
                    if (seats > 0 || seats === -1) {
                        return {
                            role: roleName,
                            seatAllocation: seats === -1 ? "unlimited" : seats,
                        };
                    }
                    return null;
                })
                    .filter((role) => role !== null);
                if (availableRoles.length > 0) {
                    ensureProduct(productsMap, productCode);
                    productsMap[productCode].availableRoles = availableRoles;
                }
            }
            // Extract licensedRegions from product data if available
            if (typeof product === "object" &&
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                product !== null &&
                "licensed_regions" in product &&
                isNonEmptyArray(product.licensed_regions)) {
                ensureProduct(productsMap, productCode);
                productsMap[productCode].licensedRegions = product.licensed_regions.map((region) => 
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                typeof region === "object" && region !== null ? region : {});
            }
        });
    }
    // 2. Extract productTier from account_contracts
    const accountContracts = rawAccountData.account_contracts;
    if (isNonEmptyArray(accountContracts)) {
        accountContracts.forEach((contract) => {
            const lineItems = contract.account_contract_line_items;
            if (!isNonEmptyArray(lineItems))
                return;
            lineItems.forEach((lineItem) => {
                const productCode = lineItem.product_code;
                const tierIdentifier = lineItem.product_tier_identifier;
                if (isValidString(productCode)) {
                    ensureProduct(productsMap, productCode);
                    if (isValidString(tierIdentifier)) {
                        productsMap[productCode].productTier = tierIdentifier;
                    }
                }
            });
        });
    }
    // 3. Extract legacyProducts grouped by product code
    if (isNonEmptyArray(rawAccountData.legacy_products)) {
        const legacyProductsMap = {};
        rawAccountData.legacy_products.forEach((legacyProduct) => {
            const { legacy_product_name, product_code } = legacyProduct;
            const legacyProductName = legacy_product_name;
            const productCode = product_code;
            if (isValidString(legacyProductName) && isValidString(productCode)) {
                if (!legacyProductsMap[productCode]) {
                    legacyProductsMap[productCode] = [];
                }
                legacyProductsMap[productCode].push(legacyProductName);
            }
        });
        // Add to products map
        Object.entries(legacyProductsMap).forEach(([productCode, names]) => {
            ensureProduct(productsMap, productCode);
            productsMap[productCode].legacyProducts = names;
        });
    }
    // 4. Extract supportLevel from product_contract
    const productContract = rawAccountData.product_contract;
    if (isNonEmptyArray(productContract === null || productContract === void 0 ? void 0 : productContract.productContractInfo)) {
        productContract.productContractInfo.forEach((info) => {
            var _a;
            const productCode = info.productCode;
            const supportLevel = (_a = info.entitlement) === null || _a === void 0 ? void 0 : _a.support_level;
            if (isValidString(productCode) && isValidString(supportLevel)) {
                ensureProduct(productsMap, productCode);
                productsMap[productCode].supportLevel = supportLevel;
            }
        });
    }
    // 5. Extract sub_products dynamically
    if (isNonEmptyArray(rawAccountData.sub_products)) {
        rawAccountData.sub_products.forEach((subProduct) => {
            const { product_code, name, value, contract_end_date, attribute } = subProduct;
            const productCode = product_code;
            const subProductName = name;
            const contractEndDate = contract_end_date;
            if (!isValidString(productCode) ||
                !isValidString(subProductName) ||
                typeof value !== "string") {
                return;
            }
            ensureProduct(productsMap, productCode);
            const attributeName = typeof (attribute === null || attribute === void 0 ? void 0 : attribute.name) === "string" ? attribute.name : null;
            const isValidDate = typeof contractEndDate === "string" &&
                contractEndDate !== "" &&
                isValidContractEndDate(contractEndDate);
            const keyName = toCamelCase(subProductName);
            // Add as dynamic sub-product
            const subProductData = Object.assign(Object.assign({ value: parseValueIfJson(value) }, (isValidDate && contractEndDate && { contractEndDate })), (attributeName !== null && { name: attributeName }));
            productsMap[productCode][keyName] = subProductData;
        });
    }
    return {
        id: rawAccountData.account_id,
        name: rawAccountData.name,
        type: rawAccountData.account_type,
        industries,
        regions,
        products: productsMap,
    };
};
/**
 * Custom hook to get identity-based account data for the current app
 *
 * This hook provides clean, product-centric data about the account
 * without exposing raw API responses. Data is organized by product code
 * for easy access to product-specific information.
 *
 * The returned structure includes:
 * - Account ID, name, type
 * - Industries and regions
 * - Products object with each product code as a key, containing:
 *   - availableRoles: from licenses
 *   - productTier: from account_contracts line items
 *   - legacyProducts: grouped by product code
 *   - supportLevel: from product_contract (support_level)
 *   - licensedRegions: from license product data
 *   - Dynamic sub-product keys (e.g., supplierKey, subNumber)
 *     All sub-products are added as camelCase keys with their data as objects
 *     contractEndDate is only included if valid (not "1900-01-01")
 *
 * @param accountId - Optional account ID. If not provided, uses the active account
 */
export const useIdentityAccountData = (accountId) => {
    try {
        useQueryClient();
    }
    catch (err) {
        throw new Error("useIdentityAccountData: No QueryClient found. " +
            "Make sure your component is wrapped with QueryClientProvider. " +
            "Example:\n\n" +
            "<QueryClientProvider client={queryClient}>\n" +
            "  <YourComponent />\n" +
            "</QueryClientProvider>");
    }
    const { accountData: rawAccountData, isLoading: accountLoading, error: accountError, } = useAccountData(accountId);
    // Use React Query to cache the transformed data
    // This prevents expensive recalculation when the same accountId is used
    const { data: transformedData, isLoading: isTransforming } = useQuery({
        queryKey: ["identityAccountInfo", rawAccountData === null || rawAccountData === void 0 ? void 0 : rawAccountData.account_id],
        queryFn: () => transformAccountData(rawAccountData),
        enabled: !accountLoading && rawAccountData !== null,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });
    // Calculate comprehensive loading state
    const isLoading = accountLoading || isTransforming;
    return {
        accountData: transformedData !== null && transformedData !== void 0 ? transformedData : null,
        isLoading,
        error: accountError,
    };
};
